import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import Modal from '../common/Modal.jsx';
import { OutlinedButton } from '../common/Buttons.jsx';
import SearchableSelectField from '../common/SearchableSelectField.jsx';
import { useToast } from '../common/Toast';
import { useConfirm } from '../common/ModalConfirmacion';
import { planService } from '../../services/planService.js';

const buildErrorMsg = (e, fallback) => {
  const data = e?.response?.data;
  if (!data) return e?.message || fallback;
  if (typeof data === 'string') return data;
  if (data?.detail) return data.detail;
  return e?.message || fallback;
};

/**
 * Gestión del plan de mantenimiento de un equipo.
 *
 * Existe porque hasta ahora la asignación era invisible y de una sola vía: se
 * hacía sola por marca/modelo/contador y no había ninguna pantalla donde ver qué
 * plan había quedado, ni forma de elegir otro. Un equipo que se quedaba sin plan
 * dejaba de avisar vencimientos y nadie tenía cómo enterarse.
 *
 * Regla de negocio (la aplica también el backend, esto es solo reflejo):
 * mientras haya un plan que corresponda por marca, modelo y contador, ESE es el
 * que manda — no se puede fijar otro a mano ni quitarlo. La asignación manual es
 * la vía de escape para el equipo huérfano (el que cambió de marca/modelo y se
 * quedó sin ningún plan), no una alternativa de uso cotidiano: si se pudiera usar
 * sobre cualquier equipo, cualquiera podría desvincular uno que ya está bien
 * gestionado y el catálogo de asignaciones se vuelve imposible de auditar.
 *
 * Props:
 *   open        {bool}   - si el modal está abierto
 *   onClose     {func}   - callback para cerrar
 *   equipoId    {number} - ID del equipo
 *   equipoLabel {string} - etiqueta descriptiva del equipo (para el título)
 *   onSuccess   {func}   - se llama tras cualquier cambio, para refrescar la pantalla
 */
const ModalPlanEquipo = ({ open, onClose, equipoId, equipoLabel, onSuccess }) => {
  const theme = useTheme();
  const { toast } = useToast();
  const { confirmar } = useConfirm();

  const [estado, setEstado] = useState(null); // { plan, plan_nombre, origen, requiere_revision, revision_motivo, plan_recomendado, plan_recomendado_nombre, hay_coincidencia }
  const [planes, setPlanes] = useState([]);
  const [seleccion, setSeleccion] = useState('');
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    if (!equipoId) return;
    setCargando(true);
    try {
      const info = await planService.obtenerEstadoPlanDeEquipo(equipoId);
      setEstado(info);
      setSeleccion(info?.plan ? String(info.plan) : '');
      // El catálogo completo de planes solo hace falta para elegir a mano, y eso
      // solo es posible cuando el equipo es huérfano (sin coincidencia). Evita el
      // pedido de red en el caso común (plan automático vigente).
      if (!info?.hay_coincidencia) {
        const listaPlanes = await planService.obtenerPlanes();
        const filas = listaPlanes?.results || listaPlanes || [];
        setPlanes(Array.isArray(filas) ? filas : []);
      } else {
        setPlanes([]);
      }
    } catch (e) {
      toast.error(buildErrorMsg(e, 'No se pudo leer el plan del equipo.'));
    } finally {
      setCargando(false);
    }
  }, [equipoId, toast]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  const trasCambiar = async (mensaje) => {
    await cargar();
    toast.success(mensaje);
    try { await onSuccess?.(); } catch {}
  };

  const onAsignar = async () => {
    if (!seleccion) { toast.warning('Elegí un plan de la lista.'); return; }
    setGuardando(true);
    try {
      await planService.asignarPlanManual(equipoId, seleccion);
      await trasCambiar('Plan asignado. Queda fijo: los cambios de marca o modelo no lo van a mover.');
    } catch (e) {
      toast.error(buildErrorMsg(e, 'No se pudo asignar el plan.'));
    } finally {
      setGuardando(false);
    }
  };

  const onRestablecer = async () => {
    setGuardando(true);
    try {
      await planService.restaurarPlanAutomatico(equipoId);
      await trasCambiar('Se aplicó el plan que corresponde por marca, modelo y contador.');
    } catch (e) {
      toast.error(buildErrorMsg(e, 'No se pudo restablecer el plan automático.'));
    } finally {
      setGuardando(false);
    }
  };

  const onConfirmarRevision = async () => {
    setGuardando(true);
    try {
      await planService.confirmarRevisionPlan(equipoId);
      await trasCambiar('Listo. El plan queda fijo para este equipo y no se vuelve a marcar.');
    } catch (e) {
      toast.error(buildErrorMsg(e, 'No se pudo confirmar el plan.'));
    } finally {
      setGuardando(false);
    }
  };

  const onQuitar = async () => {
    const ok = await confirmar({
      titulo: 'Dejar el equipo sin plan de mantenimiento',
      mensaje: 'El sistema va a dejar de calcularle próximos servicios y de avisar vencimientos '
             + 'de este equipo. Va a figurar como "no gestionado" en la lista de mantenimientos, '
             + 'y se pierde la referencia guardada del último servicio (si más adelante le '
             + 'asignás un plan, se reconstruye desde el historial). ¿Continuar?',
      labelConfirmar: 'Sí, quitar el plan',
      variante: 'danger',
    });
    if (!ok) return;
    setGuardando(true);
    try {
      await planService.quitarPlan(equipoId);
      await trasCambiar('El equipo quedó sin plan de mantenimiento.');
    } catch (e) {
      toast.error(buildErrorMsg(e, 'No se pudo quitar el plan.'));
    } finally {
      setGuardando(false);
    }
  };

  if (!open) return null;

  const hayCoincidencia = Boolean(estado?.hay_coincidencia);
  const esAutomaticoVigente = hayCoincidencia && estado?.origen === 'auto'
    && String(estado?.plan) === String(estado?.plan_recomendado);

  const cajaBase = {
    padding: 10,
    borderRadius: 6,
    border: `1px solid ${theme.colors.borderDefault}`,
    background: theme.colors.bgSecundario,
    fontSize: 12,
  };
  const cajaAlerta = {
    ...cajaBase,
    border: `1px solid ${theme.colors.warningBorder}`,
    background: theme.colors.warningBg,
    color: theme.colors.warningTxt,
  };
  const footer = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    borderTop: `1px solid ${theme.colors.borderDefault}`,
    paddingTop: 10,
  };

  return (
    <Modal title="Plan de mantenimiento del equipo" onClose={onClose} size="md" maxHeight="92vh">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {equipoLabel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 6, background: theme.colors.bgSecundario, border: `1px solid ${theme.colors.borderDefault}` }}>
            <i className="bi bi-truck" style={{ color: theme.colors.txtSecondary, fontSize: 14 }} />
            <span style={{ fontWeight: 700, fontSize: 13, color: theme.colors.txtPrincipal }}>{equipoLabel}</span>
          </div>
        )}

        {cargando || !estado ? (
          <div style={{ color: theme.colors.txtSecondary, fontSize: 12 }}>Cargando...</div>

        ) : hayCoincidencia ? (
          /* ── Hay un plan que corresponde por marca/modelo/contador: ese manda.
             No se ofrece elegir otro ni quitarlo — solo confirmarlo o adoptarlo. ── */
          <>
            {esAutomaticoVigente ? (
              <div style={cajaBase}>
                <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 13 }}>{estado.plan_nombre}</div>
                <div style={{ color: theme.colors.txtSecondary }}>
                  Corresponde por marca, modelo y tipo de contador. Asignado automáticamente.
                </div>
              </div>
            ) : (
              <div style={cajaAlerta}>
                <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 13 }}>
                  <i className="bi bi-exclamation-triangle-fill" /> Corresponde otro plan
                </div>
                <div>
                  {estado.plan_nombre ? (
                    <>Este equipo tiene <strong>{estado.plan_nombre}</strong>
                      {estado.origen === 'manual' ? ' (fijado a mano)' : ''}, pero por su marca,
                      modelo y contador le corresponde <strong>{estado.plan_recomendado_nombre}</strong>.</>
                  ) : (
                    <>Este equipo no tiene plan asignado, pero por su marca, modelo y contador le
                      corresponde <strong>{estado.plan_recomendado_nombre}</strong>.</>
                  )}
                </div>
              </div>
            )}
            <div style={footer}>
              <span />
              <div style={{ display: 'flex', gap: 6 }}>
                <OutlinedButton $size="sm" onClick={onClose} disabled={guardando}>Cerrar</OutlinedButton>
                {!esAutomaticoVigente && (
                  <OutlinedButton $size="sm" $variant="secondary" onClick={onRestablecer} disabled={guardando}>
                    <i className="bi bi-check2" /> {guardando ? 'Guardando...' : `Usar ${estado.plan_recomendado_nombre}`}
                  </OutlinedButton>
                )}
              </div>
            </div>
          </>

        ) : (
          /* ── Equipo huérfano: no hay ningún plan para su marca/modelo/contador.
             Caso puntual habilitado para elegir uno a mano. ── */
          <>
            {estado.plan ? (
              <div style={cajaAlerta}>
                <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 13 }}>
                  {estado.plan_nombre}{estado.origen === 'manual' ? ' (fijado a mano)' : ''}
                </div>
                <div>
                  No hay ningún plan que coincida con la marca, el modelo y el contador actuales
                  de este equipo.
                  {estado.requiere_revision && ' Quedó de antes de un cambio de marca o modelo.'}
                </div>
              </div>
            ) : (
              <div style={cajaAlerta}>
                <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 13 }}>
                  <i className="bi bi-exclamation-triangle-fill" /> Sin plan de mantenimiento
                </div>
                <div>
                  No hay ningún plan que coincida con marca, modelo y contador de este equipo. Podes elegir uno a mano, pero conviene que crees uno específico.
                </div>
              </div>
            )}

            <div>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4, fontWeight: 600, color: theme.colors.txtPrincipal }}>
                Asignar un plan a mano
              </label>
              <SearchableSelectField
                options={planes}
                value={seleccion}
                onChange={(value) => setSeleccion(value)}
                placeholder="Seleccionar plan..."
                getOptionValue={(o) => String(o.id)}
                getOptionLabel={(o) => o.nombre || `Plan ${o.id}`}
                showAllOnFocus
                size="sm"
                disabled={guardando}
              />
              <div style={{ marginTop: 4, fontSize: 11, color: theme.colors.txtSecondary }}>
                Para uso puntual: mientras este equipo no tenga un plan que le corresponda
                automáticamente, podés elegir cuál usar.
              </div>
            </div>

            <div style={footer}>
              <div>
                {estado.plan && (
                  <OutlinedButton $size="sm" $variant="danger" onClick={onQuitar} disabled={guardando}>
                    <i className="bi bi-x-circle" /> Quitar el plan
                  </OutlinedButton>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {estado.requiere_revision && (
                  <OutlinedButton $size="sm" onClick={onConfirmarRevision} disabled={guardando}>
                    Me quedo con este
                  </OutlinedButton>
                )}
                <OutlinedButton $size="sm" onClick={onClose} disabled={guardando}>Cerrar</OutlinedButton>
                <OutlinedButton $size="sm" $variant="secondary" onClick={onAsignar} disabled={guardando || !seleccion}>
                  <i className="bi bi-check2" /> {guardando ? 'Guardando...' : 'Asignar este plan'}
                </OutlinedButton>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ModalPlanEquipo;
